import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CategoryData,
  MerchantParams,
  OfferMerchantListResponse,
  OfferRedeemResponse,
  OfferResponse,
} from '@pv-frontend/gift-voucher-journey/interfaces/offer.interface';
import {
  AirportTransferCity,
  AddressResponse,
  AirportTransferCityResponse,
  AirportHub,
  GoogleAddressResponse,
  Country,
  CountryResponse,
  CountryDetails,
  AirportTransferParams,
  AirportTransferQuoteResponse,
  AirportTransferPayload,
  BookingResponse,
} from 'projects/visa-platform/src/app/shared/interfaces/airport-cab-booking.interface';
import { environment } from 'projects/visa-platform/src/environments/environment';
import {
  Observable,
  Subject,
  catchError,
  map,
  of,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import {
  UserCardsResponse,
  CountryData,
} from '../components/signup/signup.interface';
import {
  AirportCabBookingsDetailsResponse,
  AirportCabBookingsResponse,
  AirportCabTransferResponse,
  CancelBookingResponse,
  CreateOfferBookingResponse,
  OfferDetailsResponse,
  OfferHistoryResponseType,
} from '../interfaces';
import { OfferBookingResponse } from '../interfaces/index';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private destroy$: Subject<void> = new Subject<void>();
  public unauthorizedEventEmitter$ = new Subject<boolean>();

  public constructor(private http: HttpClient) {}

  public getOffersByMerchant(
    merchantId: string,
    needPersonalisedParam: boolean
  ): Observable<OfferResponse> {
    return this.http.get<OfferResponse>(
      environment.apiPerksServiceUrl +
        '/offers?merchant_id=' +
        merchantId +
        '&elgble=true' +
        (needPersonalisedParam ? '&fetch_voucher_claimed=true' : ''),
      { params: {} }
    );
  }

  public getOfferById(
    offerId: string,
    needPersonalisedParam: boolean
  ): Observable<OfferDetailsResponse> {
    return this.http.get<OfferDetailsResponse>(
      environment.apiPerksServiceUrl +
        '/offers/' +
        offerId +
        '?elgble=true' +
        (needPersonalisedParam ? '&fetch_voucher_claimed=true' : '')
    );
  }

  public redeem(offerId: string): Observable<OfferRedeemResponse> {
    return this.http.post<OfferRedeemResponse>(
      environment.apiPerksServiceUrl +
        '/offers/' +
        offerId +
        '/redeem?elgble=true',
      {}
    );
  }

  public fetchOffersCategories(
    paymentRequired: boolean,
    needPaymentRequiredParam: boolean
  ): Observable<CategoryData> {
    return this.http.get<CategoryData>(
      environment.apiPerksServiceUrl +
        '/categories?elgble=true' +
        (needPaymentRequiredParam
          ? '&payment_required=' + paymentRequired
          : ''),
      {
        params: { benefit_type: 'offers' },
      }
    );
  }

  public fetchMerchants(
    params: MerchantParams,
    paymentRequired: boolean,
    needPaymentRequiredParam: boolean
  ): Observable<OfferMerchantListResponse> {
    const newParams: MerchantParams & {
      payment_required?: boolean;
    } = { ...params };
    if (needPaymentRequiredParam) {
      newParams.payment_required = paymentRequired;
    }
    return this.http.get<OfferMerchantListResponse>(
      environment.apiPerksServiceUrl + '/merchants?elgble=true',
      {
        params: { ...newParams },
      }
    );
  }

  public fetchMerchantsWithPayment(
    params: MerchantParams
  ): Observable<OfferMerchantListResponse> {
    return this.http.get<OfferMerchantListResponse>(
      environment.apiPerksServiceUrl + '/merchants?elgble=true',
      {
        params: { ...params },
      }
    );
  }

  public searchMerchants(
    searchWord: string
  ): Observable<OfferMerchantListResponse> {
    return this.http.get<OfferMerchantListResponse>(
      environment.apiPerksServiceUrl + '/merchants?elgble=true',
      {
        params: { name: searchWord },
      }
    );
  }

  public fetchUserCards(params = {}): Observable<UserCardsResponse> {
    return this.http
      .get<UserCardsResponse>(
        environment.apiClientsServiceUrl + '/user/user_cards',
        {
          params: params,
        }
      )
      .pipe(
        map((res: UserCardsResponse) => res || []),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public validateCard(card_id: string): Observable<UserCardsResponse> {
    return this.http
      .post<UserCardsResponse>(
        environment.apiClientsServiceUrl + '/user_card/switch',
        { card_id: card_id }
      )
      .pipe(
        map((res) => res || []),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getOffersHistory(): Observable<OfferHistoryResponseType> {
    return this.http.get<OfferHistoryResponseType>(
      environment.apiPerksServiceUrl +
        '/redemptions?offer=true&payment_required=false'
    );
  }

  public getAirportCabBookingsHistory(): Observable<AirportCabBookingsResponse> {
    return this.http.get<AirportCabBookingsResponse>(
      environment.apiBookingServiceUrl + '/bookings?type=AirportTransfer'
    );
  }

  public cancelBooking(bookingId: string): Observable<CancelBookingResponse> {
    return this.http.post<CancelBookingResponse>(
      environment.apiBookingServiceUrl +
        `/airport_transfers/${bookingId}/cancel_booking`,
      { id: bookingId }
    );
  }

  public getCabTransferDetails(
    bookingId: string
  ): Observable<AirportCabTransferResponse> {
    return this.http.get<AirportCabTransferResponse>(
      environment.apiBookingServiceUrl + '/airport_transfers/' + bookingId
    );
  }

  public getBookingDetails(
    bookingId: string
  ): Observable<AirportCabBookingsDetailsResponse> {
    return this.http.get<AirportCabBookingsDetailsResponse>(
      environment.apiBookingServiceUrl + '/bookings/' + bookingId
    );
  }

  private getCountryData(): Observable<CountryData> {
    return this.http.get<CountryData>('https://api.country.is/').pipe(
      map((res: CountryData) => res || []),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public setCountryData(): Observable<boolean> {
    const countryData = localStorage.getItem('CF-IPCountry');
    if (countryData) {
      return of(true);
    }

    return this.getCountryData().pipe(
      map((res: CountryData) => {
        localStorage.setItem('CF-IPCountry', JSON.stringify(res.country));
        return true;
      }),
      catchError((error) => {
        console.error('Error fetching country data:', error);
        return of(false);
      })
    );
  }

  public fetchCountriesDetails(): Observable<CountryDetails[]> {
    const storedData = localStorage.getItem('allCountries');
    if (storedData) {
      return of(JSON.parse(storedData));
    }

    return this.http
      .get<CountryResponse>(`${environment.apiClientsServiceUrl}/countries`)
      .pipe(
        map((response: CountryResponse) =>
          this.transformCountries(response.data)
        ),
        tap((countryDetails: CountryDetails[]) => {
          localStorage.setItem('allCountries', JSON.stringify(countryDetails));
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  private transformCountries(countries: Country[]): CountryDetails[] {
    const countryDetails = countries.map((country) => {
      const { id, name, mobile_allowed_lengths, calling_codes, flag_url } =
        country.attributes;
      return {
        id: id,
        name: name,
        minLength: mobile_allowed_lengths[0],
        maxLength: mobile_allowed_lengths[1],
        callingCode: calling_codes[0].toString(),
        flagUrl: flag_url,
      };
    });
    return countryDetails;
  }

  public getAirportTransferCity(): Observable<AirportTransferCity> {
    return this.http
      .get<AirportTransferCityResponse>(
        environment.apiBookingServiceUrl + '/airport_transfers/cities'
      )
      .pipe(
        map((data: AirportTransferCityResponse) => ({
          data:
            data?.data?.map((city) => ({
              id: city.id,
              city_name: city.attributes.name,
            })) || [],
          message: data?.message || '',
          status: data?.status || '',
        })),
        tap((mappedData: AirportTransferCity) => {
          localStorage.setItem(
            'airportTransferCities',
            JSON.stringify(mappedData.data)
          );
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getAirportTransferHubs(cityId: string): Observable<AddressResponse> {
    return this.http
      .get<AirportHub>(
        environment.apiBookingServiceUrl + '/airport_transfers/hubs',
        { params: { airport_transfer_city_id: cityId } }
      )
      .pipe(
        map((data: AirportHub) => ({
          data: data.data.map((hub) => ({
            id: hub.id,
            isHub: true,
            hub_id: hub.id,
            ...hub.attributes,
          })),
        })),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getGoogleAddressList(
    searchWord: string
  ): Observable<GoogleAddressResponse> {
    return this.http
      .get<GoogleAddressResponse>(
        environment.apiBookingServiceUrl + '/places',
        {
          params: { query: searchWord },
        }
      )
      .pipe(
        map((data: GoogleAddressResponse) => ({
          places: data.places.map((places) => ({
            ...places,
            combinedData: `${searchWord} - ${places.name}, ${places.address}`,
          })),
        })),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public requestAirportTransferQuotes(
    payload: AirportTransferParams
  ): Observable<AirportTransferQuoteResponse> {
    return this.http
      .post<AirportTransferQuoteResponse>(
        environment.apiBookingServiceUrl + '/airport_transfers/quotes',
        { ...payload }
      )
      .pipe(
        map((res) => res || []),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getAirportTransferQuotes(): Observable<AirportTransferQuoteResponse> {
    const params = {
      airport_transfer_city_id: '27e82d83-a92e-42fb-935b-caef308e9133',
      quote_id: 'dJ0mxWbV',
    };
    return this.http
      .get<AirportTransferQuoteResponse>(
        environment.apiBookingServiceUrl + '/airport_transfers/quotes',
        { params }
      )
      .pipe(
        map((res) => res || []),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public requestAirportTransfer(
    payload: AirportTransferPayload
  ): Observable<BookingResponse> {
    return this.http
      .post<BookingResponse>(
        environment.apiBookingServiceUrl + '/airport_transfers/',
        {
          ...payload,
        }
      )
      .pipe(
        map((res) => res || []),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public createOfferBooking(
    offerId: string
  ): Observable<CreateOfferBookingResponse> {
    return this.http
      .post<OfferBookingResponse>(
        environment.apiBookingServiceUrl +
          '/offer_bookings?offer_id=' +
          offerId,
        {}
      )
      .pipe(
        takeUntil(this.destroy$),
        map((res: OfferBookingResponse) => ({
          id: res.data.id,
          status: res.data.attributes.status,
          createdAt: res.data.attributes.details.attributes.created_at,
          validTill:
            res.data.attributes.details.attributes.offer_orders[0].attributes
              .valid_till || '',
          totalPrice:
            res.data.attributes.details.attributes.fare_breakup.final_amount ||
            0,
        })),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getExclusiveDealsHistory(): Observable<AirportCabBookingsResponse> {
    return this.http.get<AirportCabBookingsResponse>(
      environment.apiBookingServiceUrl + '/bookings?type=OfferBooking'
    );
  }
}
